import { useCallback, useEffect, useRef, useState } from "react";
import { openChatStream, openChatWithToolStatus } from "@/lib/stream";
import { trackEvent, trackException } from "@/lib/telemetry";
import {
  fetchUserConversation,
  fetchUserConversations,
  saveUserConversation,
  deleteUserConversation,
  type UserConversationSummary,
} from "@/lib/api";
import type { ChatMessage, Source } from "@/types/api";

export type ChatTurnRole = "user" | "assistant";

export interface ChatTurn {
  id: string;
  role: ChatTurnRole;
  content: string;
  sources?: Source[];
  followUpQuestions?: string[];
  pending?: boolean;
  streaming?: boolean;
  error?: boolean;
  aborted?: boolean;
  startedAt?: number;
  requestId?: string | null;
}

type ChatStatus = "idle" | "loading" | "error";

const STREAMING_ENABLED = import.meta.env.VITE_CHAT_STREAMING === "true";

/**
 * Conversation history is saved server-side (Azure Table Storage, keyed by
 * the signed-in user) so it follows a user across browsers and devices —
 * see `server/azure-tables.js` (userConversations table) and
 * `POST/GET/DELETE /api/user/conversations`. Nothing is kept in localStorage.
 */
export type StoredConversation = UserConversationSummary;

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function deriveTitle(turns: ChatTurn[]): string {
  const firstUser = turns.find((t) => t.role === "user");
  if (!firstUser) return "New conversation";
  return firstUser.content.length > 60
    ? firstUser.content.slice(0, 57) + "…"
    : firstUser.content;
}

export interface ToolEvent {
  id: string;
  toolName: string;
  label: string;
  status: "running" | "done" | "error";
  summary?: string;
}

export interface UseChatResult {
  turns: ChatTurn[];
  status: ChatStatus;
  toolEvents: ToolEvent[];
  errorMessage: string | null;
  streamingEnabled: boolean;
  conversationId: string;
  savedConversations: StoredConversation[];
  sendMessage: (text: string) => Promise<void>;
  retryLast: () => Promise<void>;
  abortInFlight: () => void;
  reset: () => void;
  dismissError: () => void;
  loadConversation: (conv: StoredConversation) => void;
  deleteSavedConversation: (id: string) => void;
}

export function useChat(teamsToken?: string | null): UseChatResult {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [toolEvents, setToolEvents] = useState<ToolEvent[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string>(() => makeId());
  const [savedConversations, setSavedConversations] = useState<StoredConversation[]>([]);

  const historyRef = useRef<ChatMessage[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const retryContentRef = useRef<string | null>(null);
  const createdAtRef = useRef<string>(new Date().toISOString());
  // Keep the latest token in a ref so sendMessage always picks up the current
  // value without needing to be re-created every time the token refreshes.
  const teamsTokenRef = useRef<string | null | undefined>(teamsToken);
  useEffect(() => {
    teamsTokenRef.current = teamsToken;
  }, [teamsToken]);

  // Fetches the authoritative conversation list from the server and replaces
  // local state with it. Used on mount, whenever the tab regains focus (so
  // any past save/delete drift self-heals), and after a save the server
  // rejected (so an unpersisted optimistic entry never lingers in the UI).
  const refreshSavedConversations = useCallback(() => {
    fetchUserConversations(teamsTokenRef.current).then(setSavedConversations);
  }, []);

  // Load the signed-in user's saved conversation list on mount (and whenever
  // the Teams token changes, since that flips the auth surface). Resolves to
  // an empty list for anonymous visitors or when storage isn't configured.
  useEffect(() => {
    refreshSavedConversations();
  }, [teamsToken, refreshSavedConversations]);

  // Self-heal: re-sync with the server whenever the tab regains focus, so a
  // save/delete that silently failed while the tab was backgrounded (or in
  // another tab) never leaves the sidebar out of sync with Azure.
  useEffect(() => {
    window.addEventListener("focus", refreshSavedConversations);
    return () => window.removeEventListener("focus", refreshSavedConversations);
  }, [refreshSavedConversations]);

  // Auto-save the current conversation whenever a turn completes. The
  // sidebar list is updated optimistically first so the UI never waits on
  // the network — but if the server actually rejects the save (e.g. a 401
  // because the sign-in session hadn't fully established yet, not just a
  // network drop), we resync from the server instead of leaving a phantom
  // entry that was never really persisted.
  useEffect(() => {
    if (status === "loading") return;
    if (turns.length === 0) return;
    // Only save if the last turn is a completed assistant message.
    const last = turns[turns.length - 1];
    if (last.pending) return;

    const title = deriveTitle(turns);
    const nowIso = new Date().toISOString();
    const createdAt = createdAtRef.current;

    setSavedConversations((prev) => {
      const existing = prev.find((c) => c.id === conversationId);
      const updated: StoredConversation = {
        id: conversationId,
        title,
        createdAt: existing?.createdAt ?? createdAt,
        updatedAt: nowIso,
        turnCount: turns.length,
      };
      const rest = prev.filter((c) => c.id !== conversationId);
      return [updated, ...rest];
    });

    saveUserConversation(
      { id: conversationId, title, turns, history: historyRef.current, createdAt },
      teamsTokenRef.current,
    ).then((result) => {
      if (result === "failed") {
        trackEvent("save_conversation_failed", { conversationId });
        refreshSavedConversations();
      }
    });
  }, [turns, status, conversationId, refreshSavedConversations]);

  const abortInFlight = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === "loading") return;

      const userTurn: ChatTurn = {
        id: makeId(),
        role: "user",
        content: trimmed,
      };
      const pendingTurn: ChatTurn = {
        id: makeId(),
        role: "assistant",
        content: "",
        pending: true,
        streaming: STREAMING_ENABLED,
        startedAt: Date.now(),
      };

      retryContentRef.current = trimmed;
      setTurns((prev) => [...prev, userTurn, pendingTurn]);
      setStatus("loading");
      setErrorMessage(null);
      historyRef.current = [...historyRef.current, { role: "user", content: trimmed }];

      const controller = new AbortController();
      abortRef.current = controller;

      const startedAt = performance.now();
      trackEvent("chat_start", {
        streaming: STREAMING_ENABLED,
        historyTurns: historyRef.current.length - 1,
        messageChars: trimmed.length,
      });

      let firstTokenTracked = false;

      try {
        if (STREAMING_ENABLED) {
          const iterator = openChatStream({
            message: trimmed,
            history: historyRef.current.slice(0, -1),
            signal: controller.signal,
            teamsToken: teamsTokenRef.current,
          });

          let accumulated = "";
          let streamError: string | null = null;
          for await (const event of iterator) {
            if (event.kind === "token") {
              if (!firstTokenTracked) {
                firstTokenTracked = true;
                trackEvent("chat_first_token", {
                  ttftMs: Math.round(performance.now() - startedAt),
                });
              }
              accumulated += event.value;
              setTurns((prev) =>
                prev.map((turn) =>
                  turn.id === pendingTurn.id
                    ? { ...turn, content: accumulated, pending: false, streaming: true }
                    : turn,
                ),
              );
            } else if (event.kind === "error") {
              streamError = event.message;
              break;
            }
          }

          if (streamError) throw new Error(streamError);

          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === pendingTurn.id
                ? { ...turn, streaming: false, pending: false }
                : turn,
            ),
          );
          historyRef.current = [
            ...historyRef.current,
            { role: "assistant", content: accumulated },
          ];
          trackEvent("chat_complete", {
            streaming: true,
            totalMs: Math.round(performance.now() - startedAt),
            replyChars: accumulated.length,
          });
        } else {
          const iterator = openChatWithToolStatus({
            message: trimmed,
            history: historyRef.current.slice(0, -1),
            signal: controller.signal,
            teamsToken: teamsTokenRef.current,
          });

          let chatResponse: import("@/types/api").ChatResponse | null = null;
          let accumulated = "";
          let tokenPhaseStarted = false;

          for await (const event of iterator) {
            if (event.kind === "tool_status") {
              setToolEvents((prev) => [
                ...prev,
                {
                  id: `${event.toolName}-${Date.now()}`,
                  toolName: event.toolName,
                  label: event.label,
                  status: "running" as const,
                },
              ]);
            } else if (event.kind === "tool_complete") {
              setToolEvents((prev) =>
                prev.map((e) =>
                  e.toolName === event.toolName && e.status === "running"
                    ? { ...e, status: event.success ? ("done" as const) : ("error" as const), summary: event.summary }
                    : e,
                ),
              );
            } else if (event.kind === "token") {
              if (!tokenPhaseStarted) {
                tokenPhaseStarted = true;
                setToolEvents([]);
              }
              accumulated += event.value;
              setTurns((prev) =>
                prev.map((turn) =>
                  turn.id === pendingTurn.id
                    ? { ...turn, content: accumulated, pending: false, streaming: true }
                    : turn,
                ),
              );
            } else if (event.kind === "answer") {
              chatResponse = event.result;
            } else if (event.kind === "error") {
              throw new Error(event.message);
            }
          }
          setToolEvents([]);

          const response = chatResponse ?? { reply: accumulated || "I could not produce an answer." };
          const reply = response.reply || accumulated || "I could not produce an answer.";
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === pendingTurn.id
                ? {
                    ...turn,
                    content: reply,
                    sources: response.sources,
                    followUpQuestions: response.followUpQuestions,
                    pending: false,
                    streaming: false,
                    requestId: response.requestId ?? null,
                  }
                : turn,
            ),
          );
          historyRef.current = [
            ...historyRef.current,
            { role: "assistant", content: reply },
          ];
          trackEvent("chat_complete", {
            streaming: false,
            totalMs: Math.round(performance.now() - startedAt),
            replyChars: reply.length,
            sourceCount: response.sources?.length ?? 0,
            requestId: response.requestId,
          });
        }
        setStatus("idle");
      } catch (err) {
        setToolEvents([]);
        const isAbort = err instanceof DOMException && err.name === "AbortError";
        if (isAbort) {
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === pendingTurn.id
                ? {
                    ...turn,
                    content: turn.content || "_Generation stopped._",
                    pending: false,
                    streaming: false,
                    aborted: true,
                  }
                : turn,
            ),
          );
          trackEvent("chat_aborted", {
            totalMs: Math.round(performance.now() - startedAt),
          });
          setStatus("idle");
        } else {
          const message = err instanceof Error ? err.message : "Unknown error";
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === pendingTurn.id
                ? {
                    ...turn,
                    content: message,
                    pending: false,
                    streaming: false,
                    error: true,
                  }
                : turn,
            ),
          );
          trackEvent("chat_error", { message });
          trackException(err, { surface: "useChat.sendMessage" });
          setStatus("error");
          setErrorMessage(message);
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [status],
  );

  const retryLast = useCallback(async () => {
    const msg = retryContentRef.current;
    if (!msg || status === "loading") return;

    // Remove the last assistant turn (and the user turn before it) from both
    // the UI turns list and the API history so sendMessage re-sends cleanly.
    setTurns((prev) => {
      const lastAsstIdx = [...prev].reduceRight(
        (found, t, i) => (found === -1 && t.role === "assistant" ? i : found),
        -1,
      );
      if (lastAsstIdx < 0) return prev;
      const userIdx = [...prev]
        .slice(0, lastAsstIdx)
        .reduceRight(
          (found, t, i) => (found === -1 && t.role === "user" ? i : found),
          -1,
        );
      return prev.slice(0, userIdx >= 0 ? userIdx : lastAsstIdx);
    });
    historyRef.current = historyRef.current.slice(0, -2);

    await sendMessage(msg);
  }, [status, sendMessage]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setTurns([]);
    setStatus("idle");
    setToolEvents([]);
    setErrorMessage(null);
    historyRef.current = [];
    retryContentRef.current = null;
    createdAtRef.current = new Date().toISOString();
    setConversationId(makeId());
  }, []);

  const dismissError = useCallback(() => {
    setErrorMessage(null);
    if (status === "error") setStatus("idle");
  }, [status]);

  // Sidebar only holds conversation metadata (see StoredConversation), so
  // loading a conversation fetches its full turns/history from the server.
  const loadConversation = useCallback((conv: StoredConversation) => {
    abortRef.current?.abort();
    abortRef.current = null;
    fetchUserConversation<ChatTurn, ChatMessage>(conv.id, teamsTokenRef.current).then((full) => {
      if (!full) return;
      setTurns(full.turns);
      historyRef.current = full.history;
      createdAtRef.current = full.createdAt;
      retryContentRef.current = null;
      setStatus("idle");
      setErrorMessage(null);
      setConversationId(full.id);
    });
  }, []);

  const deleteSavedConversation = useCallback(
    (id: string) => {
      setSavedConversations((prev) => prev.filter((c) => c.id !== id));
      deleteUserConversation(id, teamsTokenRef.current).then((ok) => {
        if (!ok) {
          trackEvent("delete_conversation_failed", { conversationId: id });
          refreshSavedConversations();
        }
      });
    },
    [refreshSavedConversations],
  );

  return {
    turns,
    status,
    toolEvents,
    errorMessage,
    streamingEnabled: STREAMING_ENABLED,
    conversationId,
    savedConversations,
    sendMessage,
    retryLast,
    abortInFlight,
    reset,
    dismissError,
    loadConversation,
    deleteSavedConversation,
  };
}
